import React from 'react';
import {
  RocketLaunchIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  EnvelopeIcon,
  TableCellsIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

export const QuickStartGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <RocketLaunchIcon className="w-8 h-8 text-[#FF5722]" />
              <h1 className="text-3xl font-bold text-gray-900">
                Quick Start Guide
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Follow these simple steps to start using the Bulk SMS Platform in just a few minutes –
              from logging in, to creating contacts and sending your first SMS.
            </p>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Step overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 1
            </h2>
            <p className="text-base font-medium text-gray-900">
              Sign in and check your dashboard
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Log in with your company email, then review the dashboard cards to see SMS activity and
              available credits.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 2
            </h2>
            <p className="text-base font-medium text-gray-900">
              Add contacts and groups
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Create contacts manually or import them from files, then organize them into groups for
              quick targeting.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 3
            </h2>
            <p className="text-base font-medium text-gray-900">
              Send Quick SMS or launch a campaign
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use Quick SMS for one‑off messages, or create an SMS Campaign when you want to message
              many recipients at once.
            </p>
          </div>
        </div>

        {/* Core actions grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick SMS */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ChatBubbleLeftRightIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">Send your first Quick SMS</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                Open the <span className="font-medium">Quick SMS</span> page from the navigation.
              </li>
              <li>
                Choose a <span className="font-medium">Sender ID</span> from the dropdown.
              </li>
              <li>
                Add at least one recipient. Phone numbers must:
                <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                  <li>start with <span className="font-mono">263</span> (Zimbabwe country code)</li>
                  <li>be exactly 12 digits long, e.g. <span className="font-mono">263712345678</span></li>
                </ul>
              </li>
              <li>
                Type your SMS message, or select from an existing template if available.
              </li>
              <li>
                Click <span className="font-medium">Preview SMS</span> to review the content and
                recipients, then choose <span className="font-medium">Send Now</span>.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              Tip: The platform automatically counts characters and estimated SMS segments so you can
              control your costs.
            </p>
          </div>

          {/* Contacts & groups */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <UserGroupIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">Set up contacts and groups</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                Go to <span className="font-medium">Contacts</span> to add a new contact or view the
                contacts list.
              </li>
              <li>
                Use <span className="font-medium">New Contact</span> to capture details such as first
                name, last name, email and phone.
              </li>
              <li>
                Assign contacts to a <span className="font-medium">Group</span> (e.g. Customers,
                Staff, VIPs) so you can message them together.
              </li>
              <li>
                Use the <span className="font-medium">Import</span> options to upload contacts from
                CSV or Excel. Ensure phone columns are stored as text to avoid truncation.
              </li>
              <li>
                Review pagination and filters at the bottom of the contacts list to quickly find
                specific groups.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              Note: The platform enforces the same phone validation rules (prefix <span className="font-mono">263</span>, 12 digits) when importing or editing contacts.
            </p>
          </div>

          {/* Campaigns */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <EnvelopeIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">Create an SMS Campaign</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                Open the <span className="font-medium">SMS Campaign</span> page from the navigation.
              </li>
              <li>
                Select recipients by importing a file, choosing contacts, or using existing groups.
              </li>
              <li>
                Compose your message, or start from a saved template. You can include variables such
                as <span className="font-mono">{'{NAME}'}</span> for personalization.
              </li>
              <li>
                Review the <span className="font-medium">preview</span> – the UI will show a sample of
                phone numbers plus the total number of recipients.
              </li>
              <li>
                Schedule the campaign or send immediately, then monitor delivery from the{' '}
                <span className="font-medium">SMS Logs</span> page.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              Tip: Large campaigns may be split into multiple SMS segments depending on message
              length; keep messages concise to minimize cost.
            </p>
          </div>
        </div>

        {/* Supporting concepts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Bulk from Excel */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <TableCellsIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">Bulk SMS from Excel or CSV</h3>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Use file imports when you need to upload many phone numbers at once.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              <li>Prepare a CSV or Excel file with a dedicated column for phone numbers.</li>
              <li>Format the column as <span className="font-medium">Text</span> to prevent Excel from trimming leading digits.</li>
              <li>Upload the file via the relevant <span className="font-medium">Import</span> dialog in Contacts, Groups or Campaigns.</li>
              <li>The preview area will show up to 5 numbers and the total count being imported.</li>
            </ul>
          </div>

          {/* Sender IDs & compliance */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ShieldCheckIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">Sender IDs & compliance</h3>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Sender IDs identify who the SMS is from and may be managed by your administrator.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              <li>Select an approved <span className="font-medium">Sender ID</span> before sending.</li>
              <li>
                Follow your organization&apos;s guidelines for opt‑in, opt‑out and content
                restrictions.
              </li>
              <li>
                Where available, include unsubscribe links or keywords (for example via{' '}
                <span className="font-mono">{'{UNSUBSCRIBE}'}</span> variables) to remain compliant.
              </li>
            </ul>
          </div>

          {/* Credits & billing */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <CurrencyDollarIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">Check credits and limits</h3>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              SMS usage is driven by your allocation of credits.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              <li>From the dashboard, review your current <span className="font-medium">remaining credits</span>.</li>
              <li>Contact your administrator or use billing screens (where enabled) to request top‑ups.</li>
              <li>Keep an eye on long or multi‑segment messages, as they consume more credits.</li>
            </ul>
          </div>
        </div>

        {/* Final tips */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Best practices for successful SMS</h3>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            <li>Always verify phone numbers follow the <span className="font-mono">263XXXXXXXXX</span> format before sending.</li>
            <li>Use groups to segment your audience (e.g. by region, product, or customer type).</li>
            <li>Keep messages short, clear and action‑oriented—most users read SMS within seconds.</li>
            <li>Test campaigns with a small internal group before sending to large audiences.</li>
            <li>Review logs and dashboard stats after each campaign to learn what works best.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QuickStartGuidePage;


